import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import ScrollToTop from "@/components/ScrollToTop";
import LanguagePrompt from "@/components/LanguagePrompt";
import Header from "@/components/Header";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import useBookingReminders from "@/hooks/useBookingReminders";
import Home from "@/pages/Home";
import Search from "@/pages/Search";
import CourtDetails from "@/pages/CourtDetails";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import HostRegister from "@/pages/HostRegister";
import VerifyPhone from "@/pages/VerifyPhone";
import VerifyEmail from "@/pages/VerifyEmail";
import Bookings from "@/pages/Bookings";
import BookingDetails from "@/pages/BookingDetails";
import Profile from "@/pages/Profile";
import OwnerDashboard from "@/pages/OwnerDashboard";
import BookingFlow from "@/pages/BookingFlow";
import AddCourt from "@/pages/AddCourt";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminBookings from "@/pages/AdminBookings";
import AdminCourts from "@/pages/AdminCourts";
import AdminAmenities from "@/pages/AdminAmenities";
import AuthCallback from "@/pages/AuthCallback";
import Chats from "@/pages/Chats";
import SharedBooking from "@/pages/SharedBooking";
import PlayBrowse from "@/pages/PlayBrowse";
import PlayCreate from "@/pages/PlayCreate";
import PlaySessionDetail from "@/pages/PlaySessionDetail";
import MySessions from "@/pages/MySessions";
import FacilityPage from "@/pages/FacilityPage";
import Teams from "@/pages/Teams";
import TeamCreate from "@/pages/TeamCreate";
import TeamDetail from "@/pages/TeamDetail";
import PlayerProfile from "@/pages/PlayerProfile";
import { Toaster } from "@/components/ui/sonner";

function Router() {
  const location = useLocation();
  useBookingReminders();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <LanguagePrompt />
      <Header />
      <EmailVerifyBanner />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />} />
        <Route path="/courts/:id" element={<CourtDetails />} />
        <Route path="/courts/:id/book" element={<BookingFlow />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/host" element={<HostRegister />} />
        <Route path="/verify-phone" element={<VerifyPhone variant="page" />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/bookings"
          element={
            <ProtectedRoute>
              <Bookings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings/:bookingId"
          element={
            <ProtectedRoute>
              <BookingDetails />
            </ProtectedRoute>
          }
        />
        <Route path="/s/:bookingId" element={<SharedBooking />} />
        <Route path="/play" element={<PlayBrowse />} />
        <Route
          path="/play/create"
          element={
            <ProtectedRoute>
              <PlayCreate />
            </ProtectedRoute>
          }
        />
        <Route path="/play/:sessionId" element={<PlaySessionDetail />} />
        <Route path="/facilities/:id" element={<FacilityPage />} />
        <Route
          path="/sessions"
          element={
            <ProtectedRoute>
              <MySessions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chats"
          element={
            <ProtectedRoute>
              <Chats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/bookings"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/calendar"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/courts"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/financials"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/facilities"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/discounts"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/settings"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <OwnerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/courts/new"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <AddCourt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/courts/:courtId/edit"
          element={
            <ProtectedRoute roles={["owner", "admin"]}>
              <AddCourt />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminBookings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/courts"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminCourts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/amenities"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminAmenities />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams"
          element={
            <ProtectedRoute>
              <Teams />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams/create"
          element={
            <ProtectedRoute>
              <TeamCreate />
            </ProtectedRoute>
          }
        />
        <Route path="/teams/:id" element={<TeamDetail />} />
        <Route path="/player/:playerId" element={<PlayerProfile />} />
      </Routes>
      <Footer />
    </>
  );
}

function AppInner() {
  const { theme } = useTheme();
  return (
    <>
      <Router />
      <Toaster position="top-right" offset="5rem" theme={theme} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <ThemeProvider>
          <AuthProvider>
            <AppInner />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
